import { useEffect, useState } from "react";
import { Outlet, Navigate } from "react-router";
import { useAuthStore } from "../stores/auth-store";
import { initializeAuth, wasManualLogout } from "../lib/auth-service";
import { Spinner } from "../components/ui/Spinner";

type AuthStatus = "loading" | "authenticated" | "unauthenticated";

let initPromise: Promise<boolean> | null = null;

function deduplicatedInitializeAuth(): Promise<boolean> {
  if (!initPromise) {
    initPromise = initializeAuth().finally(() => {
      // Delay clearing so StrictMode's second mount reuses the resolved promise
      setTimeout(() => {
        initPromise = null;
      }, 1000);
    });
  }
  return initPromise;
}

export function AuthGuard() {
  const [initStatus, setInitStatus] = useState<AuthStatus>("loading");
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const hasToken = useAuthStore((s) => !!s.accessToken);
  const clearAuth = useAuthStore((s) => s.clearAuth);

  useEffect(() => {
    let cancelled = false;
    deduplicatedInitializeAuth().then((success) => {
      if (!cancelled) {
        setInitStatus(success ? "authenticated" : "unauthenticated");
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const effectiveStatus =
    initStatus === "authenticated" && !isAuthenticated
      ? "unauthenticated"
      : initStatus;

  // When the session is rejected, drop any stale persisted auth. Without this
  // the store still says isAuthenticated=true, so LoginPage immediately
  // navigates back to "/" and we ping-pong (Chrome navigation-throttle loop).
  useEffect(() => {
    if (effectiveStatus === "unauthenticated" && (isAuthenticated || hasToken)) {
      clearAuth();
    }
  }, [effectiveStatus, isAuthenticated, hasToken, clearAuth]);

  // Show content immediately if we have persisted auth while validating in background
  if (effectiveStatus === "loading" && hasToken && isAuthenticated) {
    return <Outlet />;
  }

  if (effectiveStatus === "loading") {
    return (
      <div
        className="flex min-h-screen items-center justify-center"
        role="status"
      >
        <Spinner size={32} />
        <span className="sr-only">Verifying authentication</span>
      </div>
    );
  }

  if (effectiveStatus === "unauthenticated") {
    // Hold the redirect until the stale auth is actually cleared; otherwise
    // LoginPage sees isAuthenticated=true and bounces straight back here.
    if (isAuthenticated || hasToken) {
      return (
        <div
          className="flex min-h-screen items-center justify-center"
          role="status"
        >
          <Spinner size={32} />
          <span className="sr-only">Signing out</span>
        </div>
      );
    }
    const wasRevoked =
      initStatus === "authenticated" && !wasManualLogout();
    return (
      <Navigate to={wasRevoked ? "/login?expired=true" : "/login"} replace />
    );
  }

  return <Outlet />;
}
